/**
 * Demo 统一入口页面（公共组件）
 *
 * 接收 demoRoutes 和 isDemoMode 作为 props，不依赖具体应用的文件
 */

import React, { useState, useMemo } from 'react';
import { Input, Card, Tag, Empty } from '@bedrock/components';
import { useHistory } from 'react-router-dom';

/** 固定分类集合及展示顺序 */
const CATEGORY_LIST = ['核心', '业务', '其他', '表单', '表格'] as const;

export interface DemoRouteItem {
  path: string;
  title: string;
  description?: string;
  /** 支持数组（新）或字符串（旧兼容） */
  category?: string[] | string;
  author?: string;
  date?: string;
  filePath?: string;
}

/** 将 category 统一转为数组（兼容旧字符串和新数组格式） */
function normalizeCategory(category?: string[] | string): string[] {
  if (!category) return [];
  if (Array.isArray(category)) return category;
  // 旧格式：单字符串，可能含逗号分隔
  return category
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface DemoEntryProps {
  /** Demo 路由列表 */
  demoRoutes: DemoRouteItem[];
  /** 是否启用 Demo 模式 */
  isDemoMode: boolean;
  /** 自定义标题 */
  title?: string;
  /** 自定义空状态描述 */
  emptyDescription?: string;
}

export default function DemoEntry({
  demoRoutes,
  isDemoMode,
  title = 'Demo 展示中心',
  emptyDescription = 'Demo 模式未启用',
}: DemoEntryProps) {
  const history = useHistory();
  const [searchKey, setSearchKey] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredDemos = useMemo(() => {
    let result = demoRoutes;

    // 先按分类筛选
    if (activeCategory) {
      result = result.filter((demo) =>
        normalizeCategory(demo.category).includes(activeCategory)
      );
    }

    // 再按搜索关键字过滤
    if (searchKey) {
      const lowerKey = searchKey.toLowerCase();
      result = result.filter(
        (demo) =>
          demo.path.toLowerCase().includes(lowerKey) ||
          demo.title?.toLowerCase().includes(lowerKey) ||
          normalizeCategory(demo.category).some((c) => c.toLowerCase().includes(lowerKey))
      );
    }

    return result;
  }, [searchKey, activeCategory, demoRoutes]);

  if (!isDemoMode) {
    return <Empty description={emptyDescription} />;
  }

  return (
    <div className="bg-bg-1 min-h-screen">
      <h1 className="text-2xl font-bold text-color-1 mb-6">{title}</h1>

      {/* 分类筛选条 */}
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`px-3 py-1 rounded cursor-pointer text-sm transition-colors ${
            activeCategory === null
              ? 'bg-blue-500 text-white'
              : 'bg-bg-3 text-color-2 hover:bg-bg-4'
          }`}
          onClick={() => setActiveCategory(null)}
        >
          全部
        </span>
        {CATEGORY_LIST.map((cat) => (
          <span
            key={cat}
            className={`px-3 py-1 rounded cursor-pointer text-sm transition-colors ${
              activeCategory === cat
                ? 'bg-blue-500 text-white'
                : 'bg-bg-3 text-color-2 hover:bg-bg-4'
            }`}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
          >
            {cat}
          </span>
        ))}
      </div>

      <div className="mb-6" style={{ width: 480 }}>
        <Input.Search
          placeholder="搜索 Demo（路径/标题/分类）"
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          allowClear
          size="large"
        />
      </div>

      {filteredDemos.length === 0 ? (
        <Empty description="暂无 Demo" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredDemos.map((demo) => (
            <div
              key={demo.path}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => history.push(demo.path)}
            >
              <Card className="text-sm">
                <h3 className="text-sm font-medium text-color-1 mb-1 truncate">
                  {demo.title}
                </h3>
                {demo.description && (
                  <p className="text-color-2 text-xs mb-2 line-clamp-1">
                    {demo.description}
                  </p>
                )}
                {(() => {
                  const cats = normalizeCategory(demo.category);
                  return cats.length > 0 ? (
                    <div className="flex items-center gap-1 flex-wrap">
                      {cats.map((cat) => (
                        <Tag key={cat} size="small" text={cat} />
                      ))}
                    </div>
                  ) : null;
                })()}
                <div className="mt-1 text-xs text-color-4 truncate">{demo.path}</div>
                {demo.filePath && (
                  <div className="mt-1 text-xs text-color-3 truncate" title={demo.filePath}>
                    📁 {demo.filePath}
                  </div>
                )}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
